<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 px-4"
        @click.self="close"
      >
        <transition name="pop">
          <div class="w-full max-w-3xl rounded-[32px] bg-white p-8 shadow-2xl">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs uppercase tracking-[0.6em] text-amber-500">Quick Booking</p>
                <h2 class="mt-2 text-3xl font-bold text-gray-900">Tell us your preferred plan</h2>
              </div>
              <button
                class="rounded-full bg-gray-100 p-2 text-gray-500 transition hover:bg-gray-200"
                @click="close"
                aria-label="Close"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p class="mt-4 text-sm text-gray-600">
              Share your tentative dates, group size, and desired services. Our concierge will reply via WhatsApp or email within minutes.
            </p>

            <form @submit.prevent="handleSubmit" class="mt-6 space-y-4">
              <div class="grid gap-4 md:grid-cols-2">
                <div>
                  <label class="text-xs uppercase tracking-wide text-gray-500">Full Name</label>
                  <input 
                    v-model="formData.fullName" 
                    type="text" 
                    placeholder="Muhammad Ali" 
                    :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none', errors.fullName ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-500']"
                    @blur="validateField('fullName')"
                  />
                  <p v-if="errors.fullName" class="mt-1 text-xs text-red-500">{{ errors.fullName }}</p>
                </div>
                <div>
                  <label class="text-xs uppercase tracking-wide text-gray-500">Phone / WhatsApp</label>
                  <input 
                    v-model="formData.phone" 
                    type="text" 
                    placeholder="+01 234 567 890" 
                    :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none', errors.phone ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-500']"
                    @blur="validateField('phone')"
                  />
                  <p v-if="errors.phone" class="mt-1 text-xs text-red-500">{{ errors.phone }}</p>
                </div>
              </div>
              <div class="grid gap-4 md:grid-cols-2">
                <div>
                  <label class="text-xs uppercase tracking-wide text-gray-500">Preferred Dates</label>
                  <input 
                    v-model="formData.preferredDates" 
                    type="date" 
                    placeholder="March 10 - March 20" 
                    :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none', errors.preferredDates ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-500']"
                    @blur="validateField('preferredDates')"
                  />
                  <p v-if="errors.preferredDates" class="mt-1 text-xs text-red-500">{{ errors.preferredDates }}</p>
                </div>
                <div>
                  <label class="text-xs uppercase tracking-wide text-gray-500">Group Size</label>
                  <select 
                    v-model="formData.groupSize" 
                    :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none', errors.groupSize ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-500']"
                    @blur="validateField('groupSize')"
                  >
                    <option value="">Select Group Size</option>
                    <option>Solo / Couple</option>
                    <option>Family (4+)</option>
                    <option>Group (10+)</option>
                    <option>Corporate / Delegation</option>
                  </select>
                  <p v-if="errors.groupSize" class="mt-1 text-xs text-red-500">{{ errors.groupSize }}</p>
                </div>
              </div>
              <div>
                <label class="text-xs uppercase tracking-wide text-gray-500">Services Needed</label>
                <textarea 
                  v-model="formData.servicesNeeded" 
                  rows="4" 
                  placeholder="Hotels, flights, visa, transport..." 
                  :class="['mt-2 w-full rounded-2xl border px-4 py-3 text-sm focus:outline-none', errors.servicesNeeded ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-amber-500']"
                  @blur="validateField('servicesNeeded')"
                ></textarea>
                <p v-if="errors.servicesNeeded" class="mt-1 text-xs text-red-500">{{ errors.servicesNeeded }}</p>
              </div>
            <button
              type="submit"
              class="w-full rounded-2xl bg-amber-500 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-amber-600"
            >
              Submit Request
            </button>
            </form>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: 'BookingModal',
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  data() {
    return {
      whatsappNumber: '923185073318',
      formData: {
        fullName: '',
        phone: '',
        preferredDates: '',
        groupSize: '',
        servicesNeeded: ''
      },
      errors: {
        fullName: '',
        phone: '',
        preferredDates: '',
        groupSize: '',
        servicesNeeded: ''
      }
    }
  },
  methods: {
    close() {
      this.$emit('close')
      // Reset form and errors when closing
      this.formData = {
        fullName: '',
        phone: '',
        preferredDates: '',
        groupSize: '',
        servicesNeeded: ''
      }
      this.errors = {
        fullName: '',
        phone: '',
        preferredDates: '',
        groupSize: '',
        servicesNeeded: ''
      }
    },
    validateField(fieldName) {
      this.errors[fieldName] = ''
      
      switch (fieldName) {
        case 'fullName':
          if (!this.formData.fullName || this.formData.fullName.trim() === '') {
            this.errors.fullName = 'Full name is required'
          }
          break
        case 'phone':
          if (!this.formData.phone || this.formData.phone.trim() === '') {
            this.errors.phone = 'Phone number is required'
          } else if (this.formData.phone.trim().length < 10) {
            this.errors.phone = 'Please enter a valid phone number'
          }
          break
        case 'preferredDates':
          if (!this.formData.preferredDates || this.formData.preferredDates.trim() === '') {
            this.errors.preferredDates = 'Preferred dates are required'
          }
          break
        case 'groupSize':
          if (!this.formData.groupSize || this.formData.groupSize.trim() === '') {
            this.errors.groupSize = 'Group size is required'
          }
          break
        case 'servicesNeeded':
          if (!this.formData.servicesNeeded || this.formData.servicesNeeded.trim() === '') {
            this.errors.servicesNeeded = 'Services needed is required'
          }
          break
      }
    },
    validateForm() {
      // Validate all fields
      this.validateField('fullName')
      this.validateField('phone')
      this.validateField('preferredDates')
      this.validateField('groupSize')
      this.validateField('servicesNeeded')
      
      // Check if there are any errors
      return !Object.values(this.errors).some(error => error !== '')
    },
    handleSubmit() {
      // Validate form before submission
      if (!this.validateForm()) {
        return
      }

      // Build WhatsApp message with form data
      const message = `*Quick Booking Request*\n\n` +
        `*Full Name:* ${this.formData.fullName}\n` +
        `*Phone/WhatsApp:* ${this.formData.phone}\n` +
        `*Preferred Dates:* ${this.formData.preferredDates}\n` +
        `*Group Size:* ${this.formData.groupSize}\n` +
        `*Services Needed:*\n${this.formData.servicesNeeded}`

      // Encode the message
      const encodedMessage = encodeURIComponent(message)

      // Clean the WhatsApp number
      const cleanNumber = this.whatsappNumber.replace(/[^0-9]/g, '')

      // Build WhatsApp URL
      const whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodedMessage}`

      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank')

      // Reset form after submission
      this.formData = {
        fullName: '',
        phone: '',
        preferredDates: '',
        groupSize: '',
        servicesNeeded: ''
      }
      this.errors = {
        fullName: '',
        phone: '',
        preferredDates: '',
        groupSize: '',
        servicesNeeded: ''
      }

      // Close modal after submission
      this.close()
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.pop-enter-active,
.pop-leave-active {
  transition: all 0.25s ease;
}
.pop-enter-from,
.pop-leave-to {
  opacity: 0;
  transform: scale(0.94) translateY(10px);
}
</style>

